import api from './api';
import i18n from '../i18n';
import { getLocalizedValue } from '../constants/Languages';
import { roundMoney } from '../utils/stockQuantityUtils';
import { readList, readNumber } from './listResponse';

const withStore = (storeId, config = {}) => ({ ...config, _storeId: storeId });

const pageParams = (page, pageSize) => ({
  pageIndex: Math.max(0, Number(page || 1) - 1),
  itemsPerPage: pageSize,
});

const displayName = (value) => (
  typeof value === 'string' ? value : getLocalizedValue(value, i18n.language)
);

const normalizeProduct = (product) => ({
  id: product?.id ?? product?.Id ?? product?.productId ?? product?.ProductId,
  name: displayName(product?.name ?? product?.Name),
  stockUnit: product?.stockUnit ?? product?.StockUnit ?? 'Unit',
  contentQuantity: Number(product?.contentQuantity ?? product?.ContentQuantity) || 1,
  stockOnHand: Number(product?.stockOnHand ?? product?.StockOnHand) || 0,
  lastUnitCost: roundMoney(readNumber(product?.lastUnitCost ?? product?.LastUnitCost)),
  lastPurchaseDate: product?.lastPurchaseDate ?? product?.LastPurchaseDate ?? null,
  lowStockThreshold: readNumber(product?.lowStockThreshold ?? product?.LowStockThreshold),
  isLow: Boolean(product?.isLow ?? product?.IsLow),
  isRetail: (product?.isRetail ?? product?.IsRetail) !== false,
  disabled: Boolean(product?.disabled ?? product?.Disabled),
  stockValue: readNumber(product?.stockValue ?? product?.StockValue),
  vatExempt: (product?.vatExempt ?? product?.VatExempt) !== false,
  vatRate: readNumber(product?.vatRate ?? product?.VatRate),
});

const normalizeMovement = (movement) => ({
  id: movement?.id ?? movement?.Id,
  productId: movement?.productId ?? movement?.ProductId,
  productName: displayName(movement?.productName ?? movement?.ProductName),
  movementType: movement?.movementType ?? movement?.MovementType,
  reason: movement?.reason ?? movement?.Reason ?? null,
  quantityDelta: Number(movement?.quantityDelta ?? movement?.QuantityDelta) || 0,
  balanceAfter: Number(movement?.balanceAfter ?? movement?.BalanceAfter) || 0,
  unitCost: roundMoney(readNumber(movement?.unitCost ?? movement?.UnitCost)),
  contentQuantity: Number(movement?.contentQuantity ?? movement?.ContentQuantity) || 1,
  occurredAtUtc: movement?.occurredAtUtc ?? movement?.OccurredAtUtc,
  notes: movement?.notes ?? movement?.Notes ?? '',
});

const normalizePurchase = (purchase) => ({
  id: purchase?.id ?? purchase?.Id,
  supplierId: purchase?.supplierId ?? purchase?.SupplierId ?? null,
  supplierName: purchase?.supplierName ?? purchase?.SupplierName ?? '',
  invoiceReference: purchase?.invoiceReference ?? purchase?.InvoiceReference ?? '',
  purchaseDate: purchase?.purchaseDate ?? purchase?.PurchaseDate,
  paymentMethod: purchase?.paymentMethod ?? purchase?.PaymentMethod,
  totalAmount: Number(purchase?.totalAmount ?? purchase?.TotalAmount) || 0,
  vatAmount: readNumber(purchase?.vatAmount ?? purchase?.VatAmount),
  notes: purchase?.notes ?? purchase?.Notes ?? '',
  status: purchase?.status ?? purchase?.Status,
  lines: (purchase?.lines ?? purchase?.Lines ?? []).map((line) => ({
    productId: line?.productId ?? line?.ProductId,
    productName: displayName(line?.productName ?? line?.ProductName),
    packageQuantity: Number(line?.packageQuantity ?? line?.PackageQuantity) || 0,
    unitCost: roundMoney(line?.unitCost ?? line?.UnitCost) ?? 0,
    lineTotal: Number(line?.lineTotal ?? line?.LineTotal) || 0,
    contentQuantity: Number(line?.contentQuantity ?? line?.ContentQuantity) || 1,
    stockUnit: line?.stockUnit ?? line?.StockUnit ?? 'Unit',
    vatRate: Number(line?.vatRate ?? line?.VatRate) || 0,
  })),
  productsNowNegative: (purchase?.productsNowNegative ?? purchase?.ProductsNowNegative ?? []).map((item) => ({
    productId: item?.productId ?? item?.ProductId,
    name: displayName(item?.name ?? item?.Name),
    stockOnHand: Number(item?.stockOnHand ?? item?.StockOnHand) || 0,
  })),
});

const StockService = {
  getProducts: async (storeId, { search = '', page = 1, pageSize = 20 } = {}) => {
    const response = await api.get('/stock/products', withStore(storeId, {
      params: { search: search || undefined, ...pageParams(page, pageSize) },
    }));
    const pageData = readList(response.data);
    return {
      ...pageData,
      items: pageData.items.map(normalizeProduct),
      lowStockCount: Number(response.data?.lowStockCount ?? response.data?.LowStockCount) || 0,
      negativeStockCount: Number(response.data?.negativeStockCount ?? response.data?.NegativeStockCount) || 0,
      stockValueEstimate: readNumber(response.data?.stockValueEstimate ?? response.data?.StockValueEstimate),
    };
  },

  getMovements: async (storeId, { productId, movementType, page = 1, pageSize = 20 } = {}) => {
    const response = await api.get('/stock/movements', withStore(storeId, {
      params: {
        productId: productId || undefined,
        movementType: movementType || undefined,
        ...pageParams(page, pageSize),
      },
    }));
    const pageData = readList(response.data);
    return {
      ...pageData,
      items: pageData.items.map(normalizeMovement),
    };
  },

  createAdjustment: async (storeId, adjustment) => {
    const response = await api.post('/stock/adjustments', adjustment, withStore(storeId));
    return response.data;
  },

  getPurchases: async (storeId, { page = 1, pageSize = 20 } = {}) => {
    const response = await api.get('/stock/purchases', withStore(storeId, {
      params: pageParams(page, pageSize),
    }));
    const pageData = readList(response.data);
    return {
      ...pageData,
      items: pageData.items.map(normalizePurchase),
    };
  },

  getPurchase: async (storeId, purchaseId) => {
    const response = await api.get(`/stock/purchases/${purchaseId}`, withStore(storeId));
    return normalizePurchase(response.data);
  },

  createPurchase: async (storeId, purchase) => {
    const response = await api.post('/stock/purchases', purchase, withStore(storeId));
    return normalizePurchase(response.data);
  },

  voidPurchase: async (storeId, purchaseId, voidReason) => {
    const response = await api.post(`/stock/purchases/${purchaseId}/void`, { voidReason }, withStore(storeId));
    return normalizePurchase(response.data);
  },
};

export default StockService;
